import styled, { css } from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { inlinePickCss } from "../../atoms/InlinePickTrigger";
import { fontSizes, fontWeights } from "../../../design-system/tokens/typography";
import {
  controlSize,
  type ControlSizeKey,
} from "../../../design-system/tokens/controlSizes";

export const Wrap = styled.div<{
  $invalid: boolean;
  $size: ControlSizeKey;
  $muted: boolean;
  $sentence?: boolean;
}>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: stretch;
  width: 100%;
  height: ${({ $size }) => controlSize.field[$size]};
  min-height: ${({ $size }) => controlSize.field[$size]};
  background-color: ${rawColors.structural.white};
  color: ${({ $muted }) =>
    $muted ? rawColors.text.lowEmphasis : rawColors.text.highEmphasis};
  border: 1px solid
    ${({ $invalid }) =>
      $invalid ? rawColors.accent.pink : rawColors.stroke[200]};
  border-radius: ${borderRadius.md};
  overflow: hidden;

  &:focus-within {
    border-color: ${rawColors.primary[500]};
  }

  ${({ $sentence }) =>
    $sentence &&
    css`
      ${inlinePickCss()}
      flex: none;
      padding: 0;
    `}
`;

export const Input = styled.input<{
  $compactSuffix: boolean;
  $valueLength: number;
  $sentence?: boolean;
}>`
  box-sizing: border-box;
  flex: ${({ $compactSuffix }) => ($compactSuffix ? "0 0 auto" : "1")};
  width: ${({ $compactSuffix, $valueLength }) =>
    $compactSuffix ? `calc(${$valueLength}ch + ${spacing[3]})` : "auto"};
  min-width: 0;
  border: none;
  outline: none;
  padding: ${({ $compactSuffix }) =>
    $compactSuffix
      ? `${spacing[2]} 0 ${spacing[2]} ${spacing[3]}`
      : `${spacing[2]} ${spacing[3]}`};
  ${typographyMixin("caption", 3)}
  color: inherit;
  background: transparent;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  -moz-appearance: textfield;

  ${({ $sentence, $valueLength }) =>
    $sentence &&
    css`
      flex: none;
      width: calc(${$valueLength}ch + ${spacing[4]});
      padding: 0 0 0 ${spacing[3]};
      font-size: ${fontSizes["15"]};
      font-weight: ${fontWeights.semibold};
    `}
`;

/**
 * Optional unit word rendered inside the control, right after the number
 * ("999 occurrences"). Inside rather than beside it because that is where
 * Figma puts it — a label outside the border reads as a separate field.
 */
export const Suffix = styled.span`
  display: flex;
  align-items: center;
  padding-right: ${spacing[4]};
  white-space: nowrap;
  ${typographyMixin("caption", 3)}
  color: inherit;
  white-space: nowrap;
`;

export const StepperColumn = styled.div<{ $pushEnd?: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${controlSize.iconButton.sm};
  margin-left: ${({ $pushEnd }) => ($pushEnd ? "auto" : "0")};
`;

export const StepButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0;

  &:hover:not(:disabled) {
    background-color: ${rawColors.structural[100]};
    color: ${rawColors.text.highEmphasis};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;
